import { useCallback, useEffect, useRef, useState } from "react";

interface UseClipboardCopyOptions {
  resetDelayMs?: number;
}

export type ClipboardCopyStatus = "idle" | "copied" | "failed";

// `navigator.clipboard` only exists in a secure context, and even there the
// write can be rejected, so an installation served over plain HTTP would leave
// every copy control silently doing nothing. The selection-based command still
// works in both cases, which is what this falls back to.
function copyThroughSelection(value: string): boolean {
  const field = document.createElement("textarea");
  field.value = value;
  field.setAttribute("readonly", "");
  field.style.position = "fixed";
  field.style.top = "0";
  field.style.opacity = "0";
  document.body.append(field);
  field.select();

  try {
    return document.execCommand("copy");
  } catch {
    return false;
  } finally {
    field.remove();
  }
}

export function useClipboardCopy({
  resetDelayMs = 1500,
}: UseClipboardCopyOptions = {}) {
  const [status, setStatus] = useState<ClipboardCopyStatus>("idle");
  const resetTimeoutRef = useRef<number | null>(null);

  const clearResetTimeout = useCallback(() => {
    if (resetTimeoutRef.current === null) return;
    window.clearTimeout(resetTimeoutRef.current);
    resetTimeoutRef.current = null;
  }, []);

  useEffect(() => clearResetTimeout, [clearResetTimeout]);

  const resetStatusLater = useCallback(() => {
    resetTimeoutRef.current = window.setTimeout(() => {
      setStatus("idle");
      resetTimeoutRef.current = null;
    }, resetDelayMs);
  }, [resetDelayMs]);

  const copy = useCallback(
    async (value: string) => {
      clearResetTimeout();

      try {
        await navigator.clipboard.writeText(value);
        setStatus("copied");
      } catch {
        setStatus(copyThroughSelection(value) ? "copied" : "failed");
      }

      resetStatusLater();
    },
    [clearResetTimeout, resetStatusLater],
  );

  const read = useCallback(async (): Promise<string | null> => {
    try {
      return await navigator.clipboard.readText();
    } catch {
      return null;
    }
  }, []);

  return { copied: status === "copied", copy, read, status };
}
